import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, InfoBox, Label, Toast, cn } from '@biznomi/ui-react'
import { MultiDatePicker } from '@/components/MultiDatePicker'
import {
  createLeave, getLeaveConfig, LEAVE_TYPES, isHalfDay, REASON_REQUIRED,
  type LeaveType,
} from '@/lib/api/leave'

/**
 * 휴가 신청.
 *
 * 쉴 날을 직접 고른다. 기간(시작~종료)으로 받지 않는 이유는 MultiDatePicker 주석 참고 —
 * 요약하면 주말·공휴일이 근무일인지 우리가 판정하지 않기 위해서다 (§1 경계).
 *
 * 일수는 고른 개수다. 반차만 0.5 로 친다.
 */
export default function LeaveRequest() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [type, setType] = useState<LeaveType>('ANNUAL')
  const [dates, setDates] = useState<string[]>([])
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState('')

  // 회사가 정한 종류별 규정. 못 읽으면 아무 말도 안 한다 — 짐작해서 말하면 거짓이 된다
  const { data: cfg } = useQuery({
    queryKey: ['leave', 'config'],
    queryFn: async () => (await getLeaveConfig()).data,
  })
  const types = LEAVE_TYPES.filter((t) => cfg?.enabled?.[t.value] !== false)
  const typeLabel = (v: LeaveType, fallback: string) => cfg?.label?.[v] ?? fallback

  const half = isHalfDay(type)
  // 반차는 하루뿐이다. 종류를 바꾸면 고른 날이 규칙에 안 맞을 수 있어 잘라낸다.
  const days = half ? 0.5 : dates.length
  const reasonRequired = REASON_REQUIRED.includes(type)
  // 회사 설정을 아직 못 읽었으면 undefined 다. 그때는 차감 안내를 아예 안 띄운다
  const deducts = cfg ? cfg.deduct[type] !== false : undefined

  function switchType(next: LeaveType) {
    setType(next)
    setError('')
    if (isHalfDay(next) && dates.length > 1) setDates(dates.slice(0, 1))
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (dates.length === 0) {
      setError('쉬는 날을 골라주세요')
      return
    }
    if (reasonRequired && !reason.trim()) {
      setError('어떤 사유인지 적어주세요')
      return
    }
    setBusy(true)
    setError('')
    // days 는 안 보낸다 — 서버가 정한다 (반차 0.5 / 그 외 고른 날짜 수)
    const res = await createLeave({
      type,
      dates,
      reason: reason.trim() || undefined,
    })
    setBusy(false)
    if (!res.success) {
      setError(res.error ?? '신청에 실패했습니다')
      return
    }
    qc.invalidateQueries({ queryKey: ['leave'] })
    setToast('휴가 신청이 접수되었습니다')
    setTimeout(() => navigate('/leave'), 900)
  }

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold">휴가 신청</h1>

      <InfoBox tone="info">
        쉬는 날을 직접 골라 주세요.
        고른 날만 차감합니다. 주말·공휴일도 고르면 차감합니다.
      </InfoBox>

      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label>종류</Label>
          <div className="flex flex-wrap gap-2">
            {types.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => switchType(t.value)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-sm transition-colors',
                  type === t.value
                    ? 'border-primary bg-primary/10 font-semibold text-primary'
                    : 'border-slate-200 bg-white text-slate-600',
                )}
              >
                {typeLabel(t.value, t.label)}
              </button>
            ))}
          </div>
          {deducts === false && (
            <p className="text-sm text-slate-500">
              이 종류는 연차에서 차감하지 않습니다. 회사가 정한 규정입니다.
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>{half ? '쉬는 날 (하루)' : '쉬는 날 (여러 날 고를 수 있어요)'}</Label>
          <MultiDatePicker value={dates} onChange={setDates} max={half ? 1 : undefined} />
        </div>

        {dates.length > 0 && (
          <div className="rounded-lg bg-slate-50 px-3 py-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-slate-500">고른 날</span>
              <span className="font-bold tabular-nums">{days}일</span>
            </div>
            <p className="mt-1 text-sm text-slate-600">
              {dates.map((d) => d.slice(5).replace('-', '/')).join(', ')}
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="reason">{reasonRequired ? '사유' : '사유 (선택)'}</Label>
          <textarea
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={300}
            rows={3}
            placeholder={reasonRequired ? '어떤 사유인지 적어주세요' : '필요하면 적어주세요'}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-base placeholder:text-slate-400 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          />
        </div>

        {error && (
          <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
            {error}
          </p>
        )}

        <Button type="submit" size="lg" className="w-full" loading={busy}>
          휴가 신청
        </Button>
      </form>

      {toast && <Toast message={toast} variant="success" onClose={() => setToast('')} />}
    </div>
  )
}
